// Καρτέλα «Κινήσεις ταμείου» του «Ταμείο» (migration 277, απόφαση χρήστη 19/10/2026): έξοδα, αναλήψεις και
// καταθέσεις μετρητών της ανοιχτής περιόδου, και «Μετρητά στο συρτάρι = Μετρητά + Καταθέσεις − Έξοδα −
// Αναλήψεις» — ό,τι θα τυπώσει το Ζ. Τα στοιχεία έρχονται από την ίδια αναφορά με τη «Σύνοψη»
// (report.cashMovements)· μετά από κάθε καταχώριση/ακύρωση ξαναφορτώνει όλο το «Ταμείο» (onChanged).

import React, { useEffect, useMemo, useState } from 'react';
import { Ban, CheckCircle, Loader2, Wallet, X } from 'lucide-react';
import { useBackdropDismiss } from '../../../utils/backdropDismiss';
import type { CashMovement, CashMovementKind, RegisterReport } from '../../../types/registerReport';
import { athensTime, euro, totalEuro } from '../registerFormat';
import { cashMovementsApi, type CashMovementsConfig } from './api';
import CashMovementForm from './CashMovementForm';
import { KIND_META, KIND_ORDER, drawerSign, totalsOf } from './model';
import { KindIcon, KIND_TONE } from './ui';

interface Props {
  storeId: number;
  config: CashMovementsConfig;
  report: RegisterReport | null;
  onChanged: () => void;
}

const signed = (m: CashMovement) => `${drawerSign(m) > 0 ? '+' : '−'}${euro(m.amount)}`;

const SumRow: React.FC<{ label: string; value: string; tone?: string; testId?: string }> = ({ label, value, tone, testId }) => (
  <div data-testid={testId} className="flex items-center justify-between gap-3 py-0.5 text-[14px] text-t2">
    <span>{label}</span>
    <span className={`tabular-nums font-medium ${tone || ''}`}>{value}</span>
  </div>
);

export default function CashMovementsPanel({ storeId, config, report, onChanged }: Props) {
  const [formKind, setFormKind] = useState<CashMovementKind | null>(null);
  const [voiding, setVoiding] = useState<CashMovement | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (!saved) return undefined;
    const id = window.setTimeout(() => setSaved(null), 4000);
    return () => window.clearTimeout(id);
  }, [saved]);

  const cm = report?.cashMovements || null;
  const items = useMemo(() => [...(cm?.items || [])].reverse(), [cm]);
  const totals = cm?.totals || totalsOf(cm?.items || []);
  const cash = Number(report?.cashTotal) || 0;
  const drawer = cm ? cm.drawerCash : cash;

  const onSaved = (m: CashMovement, replay: boolean) => {
    setFormKind(null);
    setSaved(`${replay ? 'Είχε ήδη καταχωρηθεί' : 'Καταχωρήθηκε'}: ${KIND_META[m.kind].label}${m.category ? ` · ${m.category}` : ''} ${signed(m)}`);
    onChanged();
  };

  return (
    <div data-testid="cash-movements-panel" className="grid gap-4">
      {!config.enabled && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-[13px] text-amber-800 dark:text-amber-300">
          Οι κινήσεις ταμείου έχουν κλείσει από τις ρυθμίσεις — βλέπεις όσες καταχωρήθηκαν στην περίοδο.
        </div>
      )}

      <div className="grid items-start gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,19rem),1fr))]">
        <div data-testid="cash-drawer-card" className="rounded-xl border border-line-strong/50 bg-surface-2 p-4 dark:bg-surface-2/40">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-t3">Μετρητά στο συρτάρι</p>
          <SumRow label="Μετρητά εισπράξεων" value={euro(cash)} />
          <SumRow label={`Καταθέσεις (${totals.counts.deposit})`} value={`+${euro(totals.deposit)}`} tone={KIND_TONE.deposit.amount} />
          <SumRow label={`Έξοδα (${totals.counts.expense})`} value={`−${euro(totals.expense)}`} tone={KIND_TONE.expense.amount} />
          <SumRow label={`Αναλήψεις (${totals.counts.withdrawal})`} value={`−${euro(totals.withdrawal)}`} tone={KIND_TONE.withdrawal.amount} />
          <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-line-strong/50 pt-2">
            <span className="text-[15px] font-bold text-t1">Στο συρτάρι</span>
            <span data-testid="cash-drawer-total" className="text-[24px] font-bold tabular-nums text-t1">{totalEuro(drawer)}</span>
          </div>
          {drawer < -0.005 && (
            <p data-testid="cash-drawer-negative" className="mt-1 text-[12px] font-medium text-amber-700 dark:text-amber-300">
              Βγήκαν περισσότερα μετρητά από όσα μπήκαν — έλεγξε τις κινήσεις για λάθος ποσό.
            </p>
          )}
          <p className="mt-1 text-[11px] text-t4">Ό,τι θα τυπώσει το Ζ. Τα μετρητά των διανομέων μετρούν όταν παραδοθούν.</p>
        </div>

        {config.canCreate && (
          <div className="grid gap-2" role="group" aria-label="Νέα κίνηση ταμείου">
            {KIND_ORDER.map(k => (
              <button key={k} type="button" data-testid={`cash-new-${k}`} onClick={() => setFormKind(k)}
                className="flex min-h-[64px] items-center gap-3 rounded-xl border border-line bg-surface-1 px-4 text-left transition-all hover:border-line-strong active:scale-[0.99] touch-manipulation">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${KIND_TONE[k].tile}`}>
                  <KindIcon kind={k} className="h-5 w-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[16px] font-semibold text-t1">{KIND_META[k].label}</span>
                  <span className="block truncate text-[12.5px] text-t3">{KIND_META[k].hint}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {saved && (
        <div role="status" data-testid="cash-saved" className="flex items-center gap-2 text-[14px] font-semibold text-emerald-600 dark:text-emerald-400">
          <CheckCircle className="h-5 w-5 shrink-0" /> {saved}
        </div>
      )}

      <div className="grid gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-t3">Κινήσεις της περιόδου</p>
        {items.length === 0 ? (
          <div className="grid place-items-center gap-2 rounded-xl border border-dashed border-line py-8 text-center">
            <Wallet className="h-8 w-8 text-t4" />
            <p className="text-[14px] text-t3">Καμία κίνηση ταμείου στην περίοδο</p>
          </div>
        ) : (
          <ul data-testid="cash-movements-list" className="grid gap-2">
            {items.map(m => (
              <li key={m.id}>
                <MovementRow m={m} canVoid={config.canVoid} onVoid={() => setVoiding(m)} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {formKind && (
        <CashMovementForm storeId={storeId} config={config} initialKind={formKind}
          onClose={() => setFormKind(null)} onSaved={onSaved} />
      )}
      {voiding && (
        <VoidDialog storeId={storeId} movement={voiding} onClose={() => setVoiding(null)}
          onDone={() => { setVoiding(null); onChanged(); }} />
      )}
    </div>
  );
}

function MovementRow({ m, canVoid, onVoid }: { m: CashMovement; canVoid: boolean; onVoid: () => void }) {
  const voided = !!m.voidedAt;
  const supplier = [m.supplierName, m.supplierVat ? `ΑΦΜ ${m.supplierVat}` : null, m.documentNumber ? `Αρ. ${m.documentNumber}` : null]
    .filter(Boolean).join(' · ');
  return (
    <div data-testid={`cash-movement-${m.id}`}
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-line bg-surface-1 px-3.5 py-2.5 ${voided ? 'opacity-60' : ''}`}>
      <div className="grid min-w-0 gap-0.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[13px] tabular-nums text-t3">{athensTime(m.at)}</span>
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[12.5px] font-semibold ${KIND_TONE[m.kind].badge}`}>
            <KindIcon kind={m.kind} className="h-3.5 w-3.5" />
            {KIND_META[m.kind].label}
          </span>
          {m.category && <span className="text-[14px] font-medium text-t1">{m.category}</span>}
        </div>
        {m.note && <span className="text-[14px] text-t2">{m.note}</span>}
        <span className="text-[12.5px] text-t3">{m.who}{supplier ? ` · ${supplier}` : ''}</span>
        {voided && (
          <span data-testid={`cash-movement-voided-${m.id}`} className="text-[12.5px] font-medium text-red-700 dark:text-red-300">
            Ακυρώθηκε{m.voidedBy ? ` από ${m.voidedBy}` : ''}{m.voidReason ? `: ${m.voidReason}` : ''} — δεν μετρά στο συρτάρι
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className={`text-[17px] font-bold tabular-nums ${voided ? 'text-t3 line-through' : KIND_TONE[m.kind].amount}`}>{signed(m)}</span>
        {canVoid && !voided && (
          <button type="button" onClick={onVoid} data-testid={`cash-void-${m.id}`} aria-label={`Ακύρωση κίνησης ${signed(m)}`}
            className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-red-300/60 bg-surface-1 px-3 text-[13px] font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30">
            <Ban className="h-4 w-4" /> Ακύρωση
          </button>
        )}
      </div>
    </div>
  );
}

const QUICK_REASONS = ['Λάθος ποσό', 'Διπλή καταχώριση', 'Λάθος είδος κίνησης'];

function VoidDialog({ storeId, movement, onClose, onDone }: { storeId: number; movement: CashMovement; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const backdrop = useBackdropDismiss(() => { if (!busy) onClose(); });

  const confirm = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await cashMovementsApi.void(storeId, movement.id, reason.trim());
      onDone();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Η ακύρωση απέτυχε');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-0 sm:p-4" {...backdrop}>
      <div role="dialog" aria-modal="true" aria-label="Ακύρωση κίνησης ταμείου" data-testid="cash-void-dialog"
        className="flex max-h-full w-full max-w-[480px] flex-col overflow-hidden bg-surface-1 shadow-xl sm:rounded-2xl sm:border sm:border-line">
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <h3 className="min-w-0 flex-1 text-[18px] font-bold text-t1">Ακύρωση κίνησης</h3>
          <button type="button" aria-label="Κλείσιμο" disabled={busy} onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface-2 text-t2 hover:bg-surface-3 disabled:opacity-40">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="grid gap-4 px-5 py-4">
          <p className="text-[14px] text-t2">
            {KIND_META[movement.kind].label}{movement.category ? ` · ${movement.category}` : ''} <span className="font-bold tabular-nums">{signed(movement)}</span>
            {' '}στις {athensTime(movement.at)} από {movement.who}. Δεν σβήνεται — μένει στη λίστα ως ακυρωμένη.
          </p>
          <div className="flex flex-wrap gap-2">
            {QUICK_REASONS.map(r => (
              <button key={r} type="button" onClick={() => setReason(r)}
                className={`min-h-[40px] rounded-lg border px-3 text-[13px] font-medium ${reason === r ? 'border-blue-600 bg-blue-600/10 text-blue-700 dark:text-blue-300' : 'border-line text-t2 hover:border-line-strong'}`}>
                {r}
              </button>
            ))}
          </div>
          <label className="grid gap-1.5">
            <span className="text-[13px] font-medium text-t2">Λόγος</span>
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={200} data-testid="cash-void-reason"
              className="h-12 w-full rounded-xl border border-line bg-surface-2 px-3.5 text-[15px] text-t1 focus:border-blue-500 focus:outline-none" />
          </label>
          {error && <div role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-[14px] text-red-700 dark:text-red-300">{error}</div>}
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={busy}
            className="min-h-[52px] rounded-xl border border-line-strong bg-surface-1 px-5 text-[15px] font-semibold text-t2 hover:text-t1 disabled:opacity-50">
            Πίσω
          </button>
          <button type="button" onClick={confirm} disabled={!reason.trim() || busy} data-testid="cash-void-confirm"
            className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-red-600 px-6 text-[15px] font-semibold text-white hover:bg-red-700 disabled:bg-surface-3 disabled:text-t3">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />}
            Ακύρωση κίνησης
          </button>
        </div>
      </div>
    </div>
  );
}
